import { useMemo } from 'react';
import useDashboardData, { resolveDateRange, browserEventsStart } from './hooks/useDashboardData';
import { mapSimEvent, mapCourseEvent, mapReminderEvent, SEND_LOG, COURSE_SEND_LOG } from './data';
import { hasTenancyScope } from './serverScope';
import { TABLE_ROW_CAP } from './exportData';

const MAPPERS = { simulations: mapSimEvent, courses: mapCourseEvent, reminders: mapReminderEvent };

// The scope to read events from the server with, or null to use the browser's
// log. A child-tenancy filter always goes to the server; so does a window that
// starts before the browser's 13 months (BD-1124: a 2024 custom range exported
// only from late 2025). selfScope is the dashboard's own-tenancy scope.
export function serverEventsScope({ scope, selfScope, period, startDate, endDate }) {
    if (hasTenancyScope(scope)) return { tenancy: Array.isArray(scope.tenancy) ? scope.tenancy : [scope.tenancy] };
    if (selfScope && resolveDateRange({ period, startDate, endDate }).start < browserEventsStart()) return selfScope;
    return null;
}

// serverEventsScope, plus the server whenever the browser log falls short of the window
// (logShort), with a detail page's own item on top. The events feed stops at RecordCap
// across the whole tenancy, so a big tenancy's log can miss some of a window's sends (BD-1020).
export function eventsScope(args, logShort, item) {
    const base = serverEventsScope(args) ?? (logShort ? args.selfScope ?? {} : null);
    return base && { ...base, ...item };
}

// Rows are newest first. A log that stopped at RecordCap on or after the window's first day
// may be missing some of the window's sends.
export function cutShort(rows, window) {
    if (!(rows?.length >= TABLE_ROW_CAP)) return false;
    return rows[rows.length - 1].dateSent >= resolveDateRange(window).start;
}

// Sends newer than the window used up part of a cut-short log's cap, so the server, asked
// for the window alone, returns more of it. For a window running to the newest send it
// would return the same rows.
export function serverHasMore(rows, window) {
    return cutShort(rows, window) && rows[0].dateSent > resolveDateRange(window).end;
}

export function capNote(rows, window) {
    if (!cutShort(rows, window)) return undefined;
    const from = new Date(`${rows[rows.length - 1].dateSent}T00:00:00Z`)
        .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    return `Showing the ${TABLE_ROW_CAP.toLocaleString('en-GB')} most recent sends, back to ${from}`;
}

function useEventRows(metric, serverScope, { period, startDate, endDate }) {
    // `scoped` = rows come from the server rather than the browser log.
    const scoped = serverScope != null;
    const q = useDashboardData(
        { metric, aggregation: 'events', period, startDate, endDate, scope: serverScope || undefined },
        { skip: !scoped },
    );
    const records = q.data?.records;
    const rows = useMemo(() => {
        if (!scoped || !Array.isArray(records)) return null;
        const out = records.map(MAPPERS[metric]).filter((e) => e.dateSent);
        out.sort((a, b) => (a.dateSent < b.dateSent ? 1 : -1));
        return out;
    }, [scoped, records, metric]);
    return {
        scoped, rows,
        isLoading: scoped && !Array.isArray(records) && (q.isFetching || q.isLoading),
        isFetching: scoped && q.isFetching,
        isError: scoped && q.isError,
        refetch: q.refetch,
    };
}

export function useTenancySimEvents(args) {
    return useEventRows('simulations', eventsScope(args, serverHasMore(SEND_LOG, args)), args);
}

export function useTenancyCourseEvents(args) {
    return useEventRows('courses', eventsScope(args, serverHasMore(COURSE_SEND_LOG, args)), args);
}

export function useSimDetailEvents(simName, args) {
    return useEventRows('simulations', eventsScope(args, cutShort(SEND_LOG, args), { sim: [simName] }), args);
}

export function useCourseDetailEvents(courseName, args) {
    return useEventRows('courses', eventsScope(args, cutShort(COURSE_SEND_LOG, args), { course: [courseName] }), args);
}

function useUserEventRows(metric, { email, period, startDate, endDate, skip }) {
    const q = useDashboardData(
        { metric, aggregation: 'events', period, startDate, endDate, scope: { user: [email] } },
        { skip },
    );
    const records = q.data?.records;
    return useMemo(() => {
        if (skip || !Array.isArray(records)) return null;
        return records.map(MAPPERS[metric]).filter((e) => e.dateSent);
    }, [skip, records, metric]);
}

export function useUserEvents({ email, period, startDate, endDate, skip = false }) {
    const args = { email, period, startDate, endDate, skip };
    return {
        sims: useUserEventRows('simulations', args),
        courses: useUserEventRows('courses', args),
        reminders: useUserEventRows('reminders', args),
    };
}
